import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ROUTES, routeFor } from '@/common/constants/routes';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { IBenefitDetail } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import { IMenhirBenefitSlug } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import { useGetFeatures } from '@/common/integrations/menhir/employee/features/features.hooks';
import { getBenefitMethods } from '@/common/utils/benefits/benefit-utils';
import { DiscoverCard } from '@/web/containers/Benefits/ItalyBenefitsPage/components/DiscoverCard/DiscoverCard';
import { UsageCard } from '@/web/containers/Benefits/ItalyBenefitsPage/components/UsageCard/UsageCard';
import {
  CardActionLink,
  IconApple,
  IconPiggyBank,
  IconReimbursement,
  IconTicketSimple,
} from '@coverflex-tech/hypernova';
import { removeFalsey } from '@coverflex-tech/hypernova-utils';
import { RelativePathString, useRouter } from 'expo-router';

const CreateVoucher = () => {
  const { t } = useTranslation();
  const router = useRouter();
  //TODO: remove this after custom descriptions
  const {
    features: { FF_FILE_BANKING_PROVIDER },
  } = useGetFeatures();
  return (
    <CardActionLink
      title={t(
        'dashboard.benefits.details.actions.create_voucher.button_label'
      )}
      description={
        <Trans
          i18nKey={`dashboard.benefits.details.actions.create_voucher.${
            FF_FILE_BANKING_PROVIDER ? 'description_temp' : 'description'
          }`}
        />
      }
      icon={<IconTicketSimple />}
      iconBadgeColor="accentPrimary40"
      onClick={() => {
        router.push(ROUTES.NEW_EDUCATION_VOUCHER);
      }}
    />
  );
};

const AskReimbursement = ({
  benefit,
  route = ROUTES.NEW_REIMBURSEMENT,
}: {
  benefit: IBenefitDetail<IMenhirBenefitSlug>;
  route?: string;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const benefitMethods = getBenefitMethods(benefit);
  const descriptionKey = benefitMethods.includes('card')
    ? 'dashboard.benefits.details.actions.reimbursement.description_employee'
    : 'dashboard.benefits.details.actions.reimbursement.description_company';

  return (
    <CardActionLink
      title={t('dashboard.benefits.details.actions.reimbursement.title')}
      description={<Trans i18nKey={descriptionKey} />}
      icon={<IconReimbursement />}
      iconBadgeColor="accentPrimary40"
      onClick={() => {
        router.push({
          pathname: route as RelativePathString,
          params: {
            benefit: benefit.slug,
          },
        });
        AnalyticsService.trackCustomEvent('ask_reimbursement_button_clicked', {
          context: 'benefit_details_page',
          category: benefit.slug,
        });
      }}
    />
  );
};

type TBuyAppleProps = {
  disabled?: boolean;
};

const BuyApple = ({ disabled = false }: TBuyAppleProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  return (
    <CardActionLink
      title={t('dashboard.benefits.details.actions.buy_apple.button_label')}
      description={
        <Trans
          i18nKey={`dashboard.benefits.details.actions.buy_apple.${
            disabled ? 'updating_catalogue_description' : 'description'
          }`}
        />
      }
      icon={<IconApple />}
      iconBadgeColor="accentPrimary40"
      onClick={() => {
        AnalyticsService.trackCustomEvent('apple_button_clicked');
        router.push(ROUTES.NEW_APPLE_ORDER);
      }}
      disabled={disabled}
    />
  );
};

const MakeContribution = ({
  benefit,
  route = ROUTES.NEW_REIMBURSEMENT,
}: {
  benefit: IBenefitDetail<IMenhirBenefitSlug>;
  route?: string;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  return (
    <CardActionLink
      title={t('dashboard.benefits.details.actions.contribution.button_label')}
      description={
        <Trans
          i18nKey={
            'dashboard.benefits.details.actions.contribution.description'
          }
        />
      }
      icon={<IconPiggyBank />}
      iconBadgeColor="accentPrimary40"
      onClick={() => {
        AnalyticsService.trackCustomEvent('make_contribution_button_clicked', {
          context: 'benefit_details_page',
          category: benefit.slug,
        });
        router.push(route as RelativePathString);
      }}
    />
  );
};

export const getBenefitActions = (
  benefit: IBenefitDetail<IMenhirBenefitSlug>,
  route?: string
): ReactElement[] => {
  let actions: (ReactElement | null)[] = [];
  switch (benefit.slug) {
    case 'gym-fitness':
    case 'health-wellbeing':
    case 'professional-training':
    case 'transportation':
    case 'senior-expenses':
    case 'parking':
    case 'summer-camp':
    case 'other-benefits':
      actions = [
        benefit.products.find((product) => product.type === 'reimbursement') ? (
          <AskReimbursement benefit={benefit} route={route} />
        ) : null,
      ];
      break;
    case 'education-training':
      actions = [
        benefit.products.find((product) => product.type === 'voucher') ? (
          <CreateVoucher />
        ) : null,
        benefit.products.find((product) => product.type === 'reimbursement') ? (
          <AskReimbursement benefit={benefit} />
        ) : null,
      ];
      break;
    case 'technology':
      const productApple = benefit.products.find(
        (product) => product.slug === 'apple'
      );
      actions = [
        productApple ? (
          <BuyApple disabled={productApple.status === 'inactive'} />
        ) : null,
        benefit.products.find((product) => product.type === 'reimbursement') ? (
          <AskReimbursement benefit={benefit} />
        ) : null,
      ];
      break;
    case 'fringe':
    case 'recreation':
    case 'it-education':
    case 'it-transportation':
    case 'family-care-services':
    case 'pension-funds':
      actions = [
        benefit.usage ? (
          <UsageCard
            usage={benefit.usage}
            key={'usage-card'}
            slug={benefit.slug}
          />
        ) : null,
        benefit.products.find((product) => product.type === 'reimbursement') ? (
          <AskReimbursement
            benefit={benefit}
            route={route}
            key={'reimbursement-card'}
          />
        ) : null,
        benefit.products.find((product) => product.type === 'voucher_card') ? (
          <DiscoverCard slug={benefit.slug} key={'discover-card'} />
        ) : null,
        benefit.products.find((product) => product.type === 'contribution') ? (
          <MakeContribution
            key={'contribution-card'}
            benefit={benefit}
            route={routeFor(ROUTES.WELFARE_CONTRIBUTION, benefit.slug)}
          />
        ) : null,
      ];
      break;
    case 'childcare-voucher':
    case 'retirement-investment':
    default:
      actions = [];
      break;
  }
  return actions.filter(removeFalsey);
};
